import { isEntityParticipantType } from '@apps/customer/domains/participant/model/entityParticipant';

type ParticipantDisplayNameSource = {
  participantName?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  legalName?: string | null;
  participantType?: number | string | null;
  entityDetails?: { legalName?: string | null } | null;
  label?: string | null;
};

function trimOrEmpty(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/**
 * Resolves a participant label for dropdowns and award forms.
 * Mirrors backend DisplayName: entity legal name, or individual "Last, First [Middle]".
 */
export function getParticipantDisplayName(
  participant: ParticipantDisplayNameSource | null | undefined
): string {
  if (!participant) {
    return '';
  }

  const fromApi = trimOrEmpty(participant.participantName) || trimOrEmpty(participant.displayName);
  if (fromApi) {
    return fromApi;
  }

  if (isEntityParticipantType(participant.participantType)) {
    const legalName =
      trimOrEmpty(participant.legalName) || trimOrEmpty(participant.entityDetails?.legalName);
    if (legalName) {
      return legalName;
    }
  }

  const firstName = trimOrEmpty(participant.firstName);
  const middleName = trimOrEmpty(participant.middleName);
  const lastName = trimOrEmpty(participant.lastName);

  if (!firstName && !middleName && !lastName) {
    return trimOrEmpty(participant.label);
  }

  if (!middleName) {
    return lastName && firstName ? `${lastName}, ${firstName}` : [firstName, lastName].filter(Boolean).join(' ');
  }

  return lastName && firstName
    ? `${lastName}, ${firstName} ${middleName}`
    : [firstName, middleName, lastName].filter(Boolean).join(' ');
}
